import SEOMetadata from '@/components/SEOMetadata';
import React, { Suspense } from 'react';
import StyledFaq from './styles/FaqPageStyles';
import ColorBends from '@/components/landing/common/Bends';
import { ChevronRight,  BookOpen, Workflow, Library } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

const loadContactSection = () => import('@/components/landing/contact/ContactSection');
const ContactSection = React.lazy(loadContactSection);
const loadBlogs = () => import('@/components/landing/home/BlogCards');
const Blogs = React.lazy(loadBlogs);
const loadFAQ = () => import('@/components/landing/pricing/FAQ');
const FAQ = React.lazy(loadFAQ);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const FaqPage: React.FC = () => {
  useIdlePrefetch([loadContactSection, loadBlogs, loadFAQ, loadGetStarted, loadFooter]);
  const navigate = useNavigate();

  return (
    <>
      <SEOMetadata
        title="AxionQuant FAQ – Frequently Asked Questions"
        description="Find answers to frequently asked questions about AxionQuant, our data, pricing, and more."
        canonicalUrl="https://axionquant.com/support"
      />
      <StyledFaq>
        <div className="hero">
          <div className="graphic">
              <ColorBends
                colors={[
                  "#ff0000",
                  "#ffff00",
                  "#00ff00",
                  "#00ffff",
                  "#0000ff",
                  "#ff00ff",
                ]}
                rotation={-10}
                speed={0.1}
                scale={1}
                frequency={1}
                warpStrength={1}
                mouseInfluence={0.5}
                parallax={0.2}
                noise={0.2}
              />
          </div>

          <div className="content">
            <h1 className="serif">Support Center</h1>
            <p>Your hub for documentation, guides, and frequently asked questions.</p>
          </div>
        </div>

        <div className="doc-cards">
          <div className="doc-card" onClick={() => navigate('/docs/')}>
            <div className="doc-icon">
              <BookOpen size={32} />
            </div>
            <div className="doc-info">
              <h2>Getting Started</h2>
              <p>A complete guide to get you up and running with the AxionQuant API.</p>
            </div>
            <ChevronRight className="doc-arrow" />
          </div>
          <div className="doc-card" onClick={() => navigate('/dashboard/playground')}>
            <div className="doc-icon">
              <Workflow size={32} />
            </div>
            <div className="doc-info">
              <h2>REST API Guide</h2>
              <p>Detailed documentation for all our API endpoints and data structures.</p>
            </div>
            <ChevronRight className="doc-arrow" />
          </div>
          <div className="doc-card" onClick={() => navigate('/docs/client-libraries/python-SDK/01-core')}>
            <div className="doc-icon">
              <Library size={32} />
            </div>
            <div className="doc-info">
              <h2>Client Libraries</h2>
              <p>Access our official and community-built libraries for your language.</p>
            </div>
            <ChevronRight className="doc-arrow" />
          </div>
        </div>

        <Suspense fallback={null}>
        <ContactSection/>


        <div className="blog-section">
          <Blogs title="From the Blog" text="Explore our latest articles, tutorials, and market insights."/>
        </div>

        <FAQ schema />

        <GetStarted
          title="Ready to Jump Back In?"
          text="Continue using Axion to make better research decisions with daily and historical data and advanced analytics."
          primary={{ title: "Dashboard", url: "/login" }}
          secondary={{ title: "Documentation", url: "/docs" }}
        />

        <Footer />
        </Suspense>
      </StyledFaq>
    </>
  );
};

export default FaqPage;
